/* eslint-disable jsx-a11y/label-has-for */
import React, { useEffect, useRef, useState } from 'react';
import SelectIcon from '../../../../icons/Select';
import BriefPill from '../../../../components/BriefPill/BriefPill';
import { useFeed } from '../../Feed';
import { useAxisData } from '../../../../contexts/AxisDataContext';
import css from './FeedCountryFilter.module.css';

const getBounds = (ref) => {
  if (!ref || !ref.current)
    return {
      top: 0,
      height: 0,
      left: 0,
      width: 0,
      bottom: 0,
      right: 0,
    };
  return ref.current.getBoundingClientRect();
};

const FeedCountryFilter = () => {
  const {
    isResultsLoading,
    feedCountries,
    setFeedCountries,
    clearSavedSearchFilter,
  } = useFeed();

  const { subscribedCountries } = useAxisData();

  // Country filter

  const ref = useRef();
  const inputRef = useRef();
  const pickerRef = useRef();

  const [isOpen, setOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const scrollHandler = () => {
      const root = document.documentElement;
      const { top, height, left, width } = getBounds(ref);
      const xCoord = left + width;
      const yCoord = top + height / 2;
      const { height: pickerHeight } = getBounds(pickerRef);
      const xOffset = xCoord + 10;
      const yOffset = yCoord - pickerHeight * 0.333;
      root.style.setProperty('--feed-country-x', `${xOffset}px`);
      root.style.setProperty('--feed-country-y', `${yOffset}px`);
    };
    scrollHandler();
    document.addEventListener('scroll', scrollHandler);
    return () => document.removeEventListener('scroll', scrollHandler);
  }, [isOpen]);

  useEffect(() => {
    setCountries(feedCountries);
    setSearch('');
  }, [isOpen]);

  useEffect(() => {
    setCountries(feedCountries);
  }, [feedCountries]);

  useEffect(() => {
    const handler = (event) => {
      const clickFilter = ref.current && ref.current.contains(event.target);
      if (!clickFilter) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    inputRef.current.focus();
  };

  const handleCountryRemove = (country) => (e) => {
    e.preventDefault();
    clearSavedSearchFilter('country');
    setFeedCountries(feedCountries.filter((c) => c.id !== country.id));
  };

  const handleCountryClick = (country) => {
    const isChecked = countries.some((c) => c.id === country.id);
    if (isChecked) {
      setCountries(countries.filter((c) => c.id !== country.id));
    } else {
      setCountries([...countries, country]);
    }
  };

  const handleClearCountries = (e) => {
    e.stopPropagation();
    clearSavedSearchFilter('country');
    setFeedCountries([]);
  };

  const handleClickCancel = () => {
    setOpen(false);
  };

  const handleClickApply = () => {
    clearSavedSearchFilter('country');
    setFeedCountries(countries);
    setOpen(false);
  };

  const isCountriesSelected = feedCountries.length > 0;
  const isDisabled = isResultsLoading;
  const hasVisibleCountries = subscribedCountries.some((country) =>
    country.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div ref={ref} data-cy="feed-country-filter" className={css.main}>
      <div
        className={css.label}
        data-status={isCountriesSelected ? 'value' : 'empty'}
        data-active={isOpen ? 'active' : 'inactive'}
        data-disabled={isDisabled}
        onClick={handleOpen}
      >
        <span className={css.placeholder}>
          <SelectIcon className={css.placeholderIcon} />
          <input
            ref={inputRef}
            type="text"
            className={css.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search countries"
          />
          {isCountriesSelected && !isOpen && !isDisabled && (
            <button
              type="button"
              className={css.clearButton}
              onClick={handleClearCountries}
            >
              Clear
            </button>
          )}
        </span>
      </div>
      <div className={css.selected}>
        {feedCountries.map((country) => (
          <BriefPill
            className={css.briefPill}
            key={country.id}
            name={country.name}
            onClose={handleCountryRemove(country)}
            isGeography
          />
        ))}
      </div>
      {isOpen && (
        <div ref={pickerRef} className={css.countryPicker}>
          <div className={css.options}>
            {subscribedCountries
              .filter((country) => {
                return country.name
                  .toLowerCase()
                  .includes(search.toLowerCase());
              })
              .map((subscribedCountry) => (
                <label
                  key={subscribedCountry.id}
                  htmlFor={subscribedCountry.id}
                  className={css.option}
                  data-cy="feed-country-filter-option"
                  data-type="feed-country-focus-item"
                >
                  <input
                    id={subscribedCountry.id}
                    className={css.optionCheckbox}
                    type="checkbox"
                    checked={countries.some(
                      (country) => country.id === subscribedCountry.id,
                    )}
                    onChange={() => handleCountryClick(subscribedCountry)}
                  />
                  <span className={css.optionLabel}>
                    {subscribedCountry.name}
                  </span>
                </label>
              ))}
            {!hasVisibleCountries && (
              <div className={css.noResults} data-type="children">
                <p className={css.noResultsSearch}>
                  No countries match &quot;{search}&quot;
                </p>
              </div>
            )}
          </div>
          <div className={css.footer}>
            <hr className={css.divider} />
            <div className={css.buttons}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={handleClickCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className={css.saveButton}
                onClick={handleClickApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedCountryFilter;
